import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import SOR from './SOR';
import BrandPerformance from './BrandPerformance';
import InventoryHealth from './InventoryHealth';
import RTORecords from './RTORecords';
import TempSor from './TempSor';
import MarkImages from './MarkImages';
import BrandReturns from './BrandReturns';
import Benchmarking from './Benchmarking';
import CategorySales from './CategorySales';
import { RiderReturns, RiderFreeShoes } from './RiderReports';
import Audit from './Audit';

// Brand Ops groups the brand/inventory analytics screens under one tab,
// each as a sub-tab. `canView(auth)` gates individual sub-tabs so access can
// diverge later; today most are core-team (the parent nav item already gates
// the whole tab to core-team+) — TempSOR stays admin-only, matching its old
// top-level access level from before it moved in here (2026-08-27).
const SUB_TABS = [
  { key: 'sor',       label: 'SOR',                 Component: SOR,              canView: () => true },
  { key: 'brandperf', label: 'Brand Performance',   Component: BrandPerformance, canView: () => true },
  { key: 'invhealth', label: 'Inventory Health',    Component: InventoryHealth,  canView: () => true },
  { key: 'categorysales', label: 'Category Sales', Component: CategorySales, canView: () => true },
  { key: 'rtorecords', label: 'RTO Records',       Component: RTORecords,       canView: () => true },
  { key: 'markimages', label: 'Mark Images',        Component: MarkImages,       canView: () => true },
  { key: 'brandreturns', label: 'Brand Returns',    Component: BrandReturns,     canView: () => true },
  { key: 'benchmarking', label: 'Benchmarking',     Component: Benchmarking,     canView: () => true },
  { key: 'riderreturns', label: 'Rider Returns',    Component: RiderReturns,     canView: () => true },
  { key: 'riderfreeshoes', label: 'Rider Free Shoes', Component: RiderFreeShoes, canView: () => true },
  { key: 'audit',     label: 'Audit',               Component: Audit,            canView: () => true },
  { key: 'tempsor',   label: 'TempSOR',             Component: TempSor,          canView: (auth) => auth?.access_level === 'admin' },
];

export default function BrandOps() {
  const { auth } = useAuth();
  const tabs = SUB_TABS.filter(t => t.canView(auth));
  const { navData, setSubTab } = useNavigation();
  const [active, setActive] = useState(
    tabs.some(t => t.key === navData?.subTab) ? navData.subTab : tabs[0]?.key);

  // ⌘K deep link: navigate('brandops', { subTab }).
  useEffect(() => {
    if (navData?.subTab && tabs.some(t => t.key === navData.subTab)) setActive(navData.subTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData]);

  useEffect(() => { if (active) setSubTab('brandops', active); }, [active, setSubTab]);
  const ActiveComponent = tabs.find(t => t.key === active)?.Component;

  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-6 pt-3 flex items-center gap-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-md transition-colors ${
              active === t.key
                ? 'text-zoddle-green border-b-2 border-zoddle-green'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {ActiveComponent && <ActiveComponent />}
    </div>
  );
}
